import { useMemo } from 'react';
import { uploadingFilesVar } from '../../store/uploadingFiles';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

export interface UseUploadDestinationsResult {
  /** The parent ID where files are being uploaded (first valid one if multiple) */
  uploadParentId: string | undefined;
  /** True if files are being uploaded to multiple different destinations */
  hasMultipleDestinations: boolean;
}

/* -------------------------------------------------------------------------- */
/*                                    Hook                                    */
/* -------------------------------------------------------------------------- */

/**
 * Hook that tracks upload destination(s)
 * When multiple batches exist with different destinations, tracks all of them
 */
export function useUploadDestinations(): UseUploadDestinationsResult {
  const uploadingFiles = uploadingFilesVar();

  return useMemo(() => {
    const batches = Object.values(uploadingFiles);
    if (batches.length === 0) {
      return { uploadParentId: undefined, hasMultipleDestinations: false };
    }

    // Get unique parentIds (filter out undefined and get unique values)
    const uniqueParentIds = [
      ...new Set(
        batches
          .map((batch) => batch.parentId)
          .filter((id): id is string => id !== undefined),
      ),
    ];

    // Use the first valid parentId found, or undefined if all are at root
    const firstValidParentId = uniqueParentIds[0];

    return {
      uploadParentId: firstValidParentId,
      hasMultipleDestinations: uniqueParentIds.length > 1,
    };
  }, [uploadingFiles]);
}
